/**
 * HrEvolutionPrint — impression « Staff evolution » au format de la page :
 *   page 1 : synthèse (hero + bridge volume/prix) + tuiles KPI + graphique 24 mois
 *   page 2 : tableau de données mensuel
 *
 * Réutilise les composants/logique partagés (HeroCard, KpiTile, EvolutionChart,
 * evolutionModel) → visuel identique à l'écran.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { listHrContracts, getPeopleEvolutionReading } from '../../../api/hr';
import HeroCard from '../HeroCard';
import KpiTile from '../KpiTile';
import EvolutionChart from '../EvolutionChart';
import { buildEvolutionModel, buildEvolutionSeries } from '../evolutionModel';
import PrintReportHeader from './PrintReportHeader';
import '../HrPage.css';

export default function HrEvolutionPrint({ activeTenant, refYear, editedAt, onDataReady }) {
  const { t } = useTranslation();
  const tenantId = activeTenant?.id;
  const prevYear = refYear - 1;
  const [contracts, setContracts] = useState([]);
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    if (!tenantId) return;
    Promise.all([
      listHrContracts(tenantId),
      getPeopleEvolutionReading(tenantId, refYear),
    ]).then(([c, r]) => {
      if (!c.error) setContracts(c.data || []);
      if (r && !r.error && r.data) setMetrics(r.data.metrics || null);
    }).finally(() => onDataReady && onDataReady('evolution'));
  }, [tenantId, refYear]); // eslint-disable-line

  const model = useMemo(() => buildEvolutionModel(metrics, prevYear, refYear), [metrics, prevYear, refYear]);
  const series = useMemo(
    () => buildEvolutionSeries(
      contracts, prevYear, refYear, model.csRateN,
      metrics?.monthly_mass_n_1, metrics?.monthly_mass_n,
    ),
    [contracts, prevYear, refYear, model.csRateN, metrics],
  );
  return (
    <section className="ppl-print__section ppl-print__section--evolution">
      <PrintReportHeader category="People" title={t('phrases:p_58d5412ae0')} year={refYear} tenant={activeTenant} editedAt={editedAt} />

      <HeroCard {...model.hero} bridge={model.bridge} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8, marginBottom: 14 }}>
        {model.tiles.map((t, i) => (
          <KpiTile key={i} prevYear={prevYear} {...t} />
        ))}
      </div>

      <EvolutionChart {...series} prevYear={prevYear} refYear={refYear} />
    </section>
  );
}
